<template>
    <form
        :class="[$style.form, { [$style.modal]: viewMode === 'modal' }]"
        @submit.prevent="emit('submit', formData)"
    >
        <TextInput
            id="form-email"
            v-model="formData.email"
            required
            :disabled="areInputsDisabled"
            size="m"
            name="email"
            type="email"
            :labelText="$tr('contactForm.email')"
            :placeholder="$tr('contactForm.email')"
        />
        <TextInput
            is="textarea"
            id="form-message"
            v-model="formData.message"
            type="text"
            required
            :disabled="areInputsDisabled"
            withHiddenLabel
            name="message"
            :labelText="$tr('contactForm.message')"
            :placeholder="$tr('contactForm.message')"
        />
        <Turnstile
            :class="props.isTurnstileCentered && $style.centered_turnstile_override"
            @tokenReceived="onTurnstileTokenReceived"
            @error="onTurnstileError"
        />
        <div :class="$style.submitContainer">
            <Button
                :class="[
                    $style.submitBtn_override,
                    buttonProps.class,
                    { [$style.submitBtnWithCheckboxes_override]: props.withCheckboxes },
                ]"
                size="l"
                :color="buttonProps.color"
                :type="buttonProps.type"
                :leftIcon="buttonProps.iconLeft"
                :disabled="buttonProps.disabled"
                :interfaceTarget="buttonProps.interfaceTarget"
                :label="buttonProps.title"
                @click="buttonProps.onClick"
            />

            <Typography
                is="p"
                v-if="!props.withCheckboxes"
                :size="viewMode === 'modal' ? 'xs' : 's'"
                :class="$style.submitHint"
            >
                <span :class="$style.mutedText">
                    {{ $tr('contactForm.agreementTextStart') }}
                </span>
                <Link
                    to="/termsofuse"
                    color="purple-500"
                    target="_blank"
                >
                    {{ $tr('contactForm.termsOfUse') }}
                </Link>
                <span :class="$style.mutedText">
                    {{ $tr('contactForm.agreementTextAnd') }}
                </span>
                <Link
                    :to="privacyPolicyRoute.to"
                    :externalTo="privacyPolicyRoute.externalTo"
                    color="purple-500"
                    target="_blank"
                >
                    {{ privacyPolicyRoute.title }}
                </Link>
            </Typography>
        </div>

        <div v-if="props.withCheckboxes">
            <div :class="$style.checkboxRow">
                <Checkbox
                    id="first-checkbox"
                    size="s"
                    @change="(event: Event) => (firstCheckboxValue = (event.target as HTMLInputElement).checked)"
                />
                <Typography
                    is="p"
                    :size="viewMode === 'modal' ? 'xs' : 's'"
                    :class="[$style.submitHint, $style.mutedText]"
                >
                    <slot name="firstCheckboxText" />
                </Typography>
            </div>
            <div :class="$style.checkboxRow">
                <Checkbox
                    id="second-checkbox"
                    size="s"
                    @change="(event: Event) => (secondCheckboxValue = (event.target as HTMLInputElement).checked)"
                />
                <Typography
                    is="p"
                    :size="viewMode === 'modal' ? 'xs' : 's'"
                    :class="[$style.submitHint, $style.mutedText]"
                >
                    <slot name="secondCheckboxText" />
                </Typography>
            </div>
        </div>
    </form>
</template>
<script setup lang="ts">
import { computed, ref, useCssModule } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import Checkbox from '@/components/Checkbox/Checkbox.vue'
import Link from '@/components/Link.vue'
import TextInput from '@/components/TextInput/TextInput.vue'
import Turnstile from '@/components/Turnstile/Turnstile.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { PRIVACY_POLICY_NAVIGATION_ITEM } from '@/router/navigation-constants'
import { type FormSubmitData } from '@/types'
import { type RequestStatus } from '@/utils/requests/types'

const props = defineProps<{
    submitStatus: RequestStatus
    viewMode?: 'modal' | 'default'
    withCheckboxes?: boolean
    isTurnstileCentered?: boolean
}>()
const emit = defineEmits<{
    submit: [formState: FormSubmitData]
    successButtonClicked: []
    formReset: []
    turnstileReady: [token: string]
}>()
const classes = useCssModule()
const formData = defineModel<FormSubmitData>({ required: true })
const tr = useTr()

const isTurnstileReady = ref(false)

function onTurnstileTokenReceived(token: string) {
    isTurnstileReady.value = true
    emit('turnstileReady', token)
}

function onTurnstileError() {
    isTurnstileReady.value = false
}

const privacyPolicyRoute = computed(() => PRIVACY_POLICY_NAVIGATION_ITEM(tr))

const hasSuccessfullySubmitted = computed(() => props.submitStatus === 'ok')
const isError = computed(() => props.submitStatus === 'error')
const isSending = computed(() => props.submitStatus === 'pending')
const areInputsDisabled = computed(() => isSending.value || hasSuccessfullySubmitted.value)
const hasMissingValues = computed((): boolean => {
    const hasFormMissingValues = Object.values(formData.value).some((value) => !value.trim())
    const hasUncheckedCheckboxes = props.withCheckboxes && (!firstCheckboxValue.value || !secondCheckboxValue.value)

    return hasFormMissingValues || hasUncheckedCheckboxes || !isTurnstileReady.value
})

const firstCheckboxValue = ref(false)
const secondCheckboxValue = ref(false)

const buttonProps = computed(() => {
    if (hasSuccessfullySubmitted.value) {
        return {
            // successs button resets the form and is never disabled
            disabled: false,
            color: 'lime' as const,
            iconLeft: 'check' as const,
            title: tr('contactForm.messageSent'),
            type: undefined,
            interfaceTarget: 'submited-form',
            onClick: () => emit('formReset'),
        }
    }

    return {
        disabled: areInputsDisabled.value || hasMissingValues.value,
        color: isError.value ? ('rose-400' as const) : ('purple-400' as const),
        type: 'submit' as const,
        interfaceTarget: isError.value ? 'error-form' : 'submit-form',
        title: isError.value ? tr('contactForm.error') : tr('contactForm.submit'),
        class: isError.value ? classes.shakeError : undefined,
    }
})
</script>

<style module>
.form {
    display: flex;
    flex-direction: column;
    gap: var(--gap-16);
}

.mutedText {
    color: var(--white-40);
}

.submitContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--gap-16);
}

.checkboxRow {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--gap-16);
    margin-bottom: var(--gap-16);
    margin-left: var(--gap-32);
    flex-flow: row nowrap;
}

.submitBtn_override {
    flex-basis: 240px;
    width: 240px;
    flex-grow: 1;
}

.submitBtnWithCheckboxes_override {
    max-width: 326px;
}

.centered_turnstile_override {
    align-self: center;
}

.shakeError {
    animation: shake 0.15s ease-in-out 0s 2;
}

.submitHint {
    flex-grow: 1;
    text-align: center;
    margin-bottom: 0 !important;
}

@media (--desktop-medium-plus) {
    .submitHint {
        flex-grow: 1;
        text-align: left;
    }
}

.modal .submitHint {
    text-align: center;
}

@keyframes shake {
    0% {
        transform: translate3d(0rem, 0, 0);
    }
    25% {
        transform: translate3d(10px, 0, 0);
    }
    75% {
        transform: translate3d(-10px, 0, 0);
    }
    100% {
        transform: translate3d(0rem, 0, 0);
    }
}
</style>
